import { z } from 'zod';

/**
 * How a finding's file is classed, by PATH alone.
 *
 * Its own module because both ends need it: the shortlist classes retained
 * occurrences for display, and the producer classes the uncapped finding list
 * when it measures produced-versus-retained dimensions. Produced and retained
 * counts that disagreed about which files are tests would subtract to a
 * difference nobody could act on. One definition, one disclosed version, both
 * ends — and no import cycle between the capture and shortlist contracts.
 */
export const FindingFileClassSchema = z.enum(['source', 'test', 'generated']);
export type FindingFileClass = z.infer<typeof FindingFileClassSchema>;

/** The disclosed name of the heuristic below, carried beside every use of it. */
export const FINDING_PATH_CLASSIFICATION = 'path-heuristic-v1';

/**
 * Path heuristic only: generated wins over test; source is the fallback.
 *
 * Never content inspection: a file is classed by where it sits and what it is
 * called, so a `specs/` directory of real source is misclassified by design.
 * That is why every surface reports the classification by name rather than
 * presenting the class as a fact about the file.
 */
export function classifyFindingPath(path: string): FindingFileClass {
  if (
    /(^|\/)(_generated|generated|generated-files)(\/|$)|\.generated\./u.test(
      path,
    )
  )
    return 'generated';
  if (
    /(^|\/)(__tests__|tests?|specs?|e2e)(\/|$)|\.(test|spec)\.|(^|\/)test_[^/]+\.py$|_test\.py$/u.test(
      path,
    )
  )
    return 'test';
  return 'source';
}
